import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  buildOrganizationSchema,
  buildWebsiteSchema,
  buildSoftwareApplicationSchema,
  buildServiceSchema,
  buildContentServiceSchema,
  buildFaqSchemaFromItems,
  buildBreadcrumbSchema,
  buildTechArticleSchema,
  buildBlogPostingSchema,
  buildWebPageSchema,
  buildPersonSchema,
} from './seo/schema';

function StructuredData({
  page = 'home',
  article = null,
  contentPage = null,
  faqItems = null,
  locale = 'fr',
}) {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://leagora.io';
  const siteName = process.env.REACT_APP_SITE_NAME || 'Leagora';

  const sectionLabels = {
    hub: locale === 'en' ? 'Video infrastructure' : 'Infrastructure vidéo',
    'hub-en': 'Video infrastructure',
    articles: locale === 'en' ? 'Articles' : 'Articles',
    comparatifs: locale === 'en' ? 'Comparisons' : 'Comparatifs',
    'comparatifs-en': 'Comparisons',
    solutions: locale === 'en' ? 'Solutions' : 'Solutions',
    'solutions-en': 'Solutions',
    secteurs: locale === 'en' ? 'Sectors' : 'Secteurs',
    pages: locale === 'en' ? 'Trust' : 'Confiance',
  };

  const resolvedContentPage = contentPage || (page === 'article' ? article : null);

  const toCanonicalUrl = (path) => {
    if (!path || path === '/') return `${siteUrl}/`;
    const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
    const withTrailingSlash = withLeadingSlash.endsWith('/')
      ? withLeadingSlash
      : `${withLeadingSlash}/`;
    return `${siteUrl}${withTrailingSlash}`;
  };

  const getPageUrl = () => {
    if (resolvedContentPage?.path) {
      return toCanonicalUrl(resolvedContentPage.path);
    }
    if (page === 'home' && locale === 'en') {
      return `${siteUrl}/en/`;
    }
    if (page === 'home') {
      return `${siteUrl}/`;
    }
    return toCanonicalUrl(`/${page}/`);
  };

  const pageUrl = getPageUrl();
  const contentOgImage =
    resolvedContentPage?.section && resolvedContentPage?.slug
      ? `${siteUrl}/og/${resolvedContentPage.section === 'hub-en' ? 'hub' : resolvedContentPage.section}/${resolvedContentPage.slug}.png`
      : null;

  const organizationSchema = buildOrganizationSchema({ siteUrl, siteName });
  const websiteSchema = buildWebsiteSchema({ siteUrl, siteName, locale });
  const softwareSchema = buildSoftwareApplicationSchema({ siteUrl, siteName });
  const serviceSchema = buildServiceSchema({ siteUrl, siteName });
  const homeFaqSchema = page === 'home' ? buildFaqSchemaFromItems(faqItems) : null;
  const pageFaqSchema = page !== 'home' ? buildFaqSchemaFromItems(faqItems) : null;
  const personSchema = buildPersonSchema({ siteName, siteUrl });

  const breadcrumbSchema = buildBreadcrumbSchema({
    siteUrl,
    page,
    contentPage: resolvedContentPage,
    pageUrl,
    sectionLabels,
    locale,
  });

  const isHubContent =
    page === 'content' &&
    resolvedContentPage &&
    ['hub', 'hub-en'].includes(resolvedContentPage.section);

  const techArticleSchema = isHubContent
    ? buildTechArticleSchema({
        siteUrl,
        siteName,
        contentPage: resolvedContentPage,
        pageUrl,
        locale: resolvedContentPage.locale || locale,
      })
    : null;

  const blogPostingSchema =
    page === 'content' &&
    resolvedContentPage &&
    ['articles', 'comparatifs', 'comparatifs-en'].includes(resolvedContentPage.section)
      ? buildBlogPostingSchema({
          siteUrl,
          siteName,
          contentPage: resolvedContentPage,
          pageUrl,
          locale: resolvedContentPage.locale || locale,
          ogImage: contentOgImage,
        })
      : null;

  const solutionSectorSchema =
    page === 'content' &&
    resolvedContentPage &&
    ['solutions', 'solutions-en', 'secteurs'].includes(resolvedContentPage.section)
      ? buildWebPageSchema({
          siteUrl,
          siteName,
          contentPage: resolvedContentPage,
          pageUrl,
          type: 'WebPage',
          locale: resolvedContentPage.locale || locale,
        })
      : null;

  const trustPageSchema =
    page === 'content' &&
    resolvedContentPage &&
    resolvedContentPage.section === 'pages'
      ? buildWebPageSchema({
          siteUrl,
          siteName,
          contentPage: resolvedContentPage,
          pageUrl,
          type: resolvedContentPage.slug === 'a-propos' ? 'AboutPage' : 'WebPage',
          locale: resolvedContentPage.locale || locale,
        })
      : null;

  const contentServiceSchema = isHubContent
    ? buildContentServiceSchema({
        siteUrl,
        siteName,
        contentPage: resolvedContentPage,
        pageUrl,
      })
    : null;

  const articleFaqSchema = buildFaqSchemaFromItems(resolvedContentPage?.faq);

  const schemas = [
    organizationSchema,
    websiteSchema,
    ...(page === 'home' ? [softwareSchema, serviceSchema, homeFaqSchema, personSchema] : []),
    breadcrumbSchema,
    contentServiceSchema,
    techArticleSchema,
    blogPostingSchema,
    solutionSectorSchema,
    trustPageSchema,
    articleFaqSchema,
    pageFaqSchema,
  ].filter(Boolean);

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={`${schema['@type']}-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default StructuredData;
